import React from 'react';
import { useAtomValue } from 'jotai';
import {
  layoutModeAtom,
  sidebarCollapsedAtom,
  chatSidebarCollapsedAtom,
  chatSidebarWidthAtom
} from '../../../../atoms/v3/uiAtoms';
import { chatInputFocusedAtom } from '../../../../atoms/v3/chatInputAtoms';
import { getPlatformInfo } from '../../../../utils/platformUtils';

type BottomContainerMode =
  | 'mobile'           // 移动端模式，需要避让TabBar
  | 'desktop-main'     // 桌面端主内容区域，适应中栏宽度
  | 'desktop-sidebar'; // 桌面端侧边栏，适应侧边栏宽度

interface IOSBottomContainerProps {
  children: React.ReactNode;
  className?: string;
  mode?: BottomContainerMode;
  // 是否在聊天页面（影响TabBar隐藏逻辑）
  isChatPage?: boolean;
}

/**
 * iOS 底部容器组件
 * 统一处理底部组件的避让逻辑，包括：
 * - iOS 安全区域避让
 * - TabBar 避让
 * - 桌面端侧边栏避让
 * - 键盘弹出时的动态调整
 */
export const IOSBottomContainer: React.FC<IOSBottomContainerProps> = ({
  children,
  className = '',
  mode,
  isChatPage = false
}) => {
  const layoutMode = useAtomValue(layoutModeAtom);
  const chatInputFocused = useAtomValue(chatInputFocusedAtom);
  const sidebarCollapsed = useAtomValue(sidebarCollapsedAtom);
  const chatSidebarCollapsed = useAtomValue(chatSidebarCollapsedAtom);
  const chatSidebarWidth = useAtomValue(chatSidebarWidthAtom);

  // 获取平台信息
  const platformInfo = getPlatformInfo();
  const isMobile = platformInfo.isIOS || platformInfo.isAndroid;

  // 自动推断模式
  const containerMode = mode || (layoutMode === 'desktop' ? 'desktop-main' : 'mobile');
  
  // 判断是否应该隐藏TabBar（只在聊天页面且输入框有焦点时）
  const shouldHideTabBar = isChatPage && chatInputFocused && isMobile;

  // 动态计算底部偏移量
  const getBottomClass = () => {
    switch (containerMode) {
      case 'desktop-main':
      case 'desktop-sidebar':
        return 'bottom-0'; // 桌面模式不需要避让TabBar

      case 'mobile':
      default:
        if (shouldHideTabBar) {
          // 键盘弹出且TabBar隐藏时，只保留安全区域偏移
          return 'bottom-[env(safe-area-inset-bottom)]';
        }
        // 默认情况：TabBar显示时的偏移
        return 'bottom-[calc(4rem+env(safe-area-inset-bottom))]';
    }
  };

  // 动态计算定位和边距
  const getPositionAndMargins = () => {
    switch (containerMode) {
      case 'desktop-main':
        // 桌面主内容区域：固定定位，避让左右侧边栏
        return {
          position: 'fixed left-0 right-0',
          style: {
            marginLeft: sidebarCollapsed ? '4.375rem' : '16.25rem',
            marginRight: chatSidebarCollapsed ? '0' : `${chatSidebarWidth}rem`
          }
        };

      case 'desktop-sidebar':
        // 桌面侧边栏：相对定位，适应容器宽度
        return {
          position: 'absolute left-0 right-0',
          style: {}
        };

      case 'mobile':
      default:
        // 移动端：固定定位，全宽
        return {
          position: 'fixed left-0 right-0',
          style: {}
        };
    }
  };

  const { position, style } = getPositionAndMargins();

  return (
    <div
      className={`
        ${position}
        ${getBottomClass()}
        z-40
        ${className}
      `}
      style={style}
    >
      {children}
    </div>
  );
};
